import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { RangeDateInput } from '@core/components/Inputs';
import { SimpleTableBase } from '@core/components/Table';
import { TableActionButtonDelete } from '@core/components/TableAction';
import { DataBR } from '@format/DATA';
import { Button, Tooltip, Typography } from '@material-ui/core';
import { cancelarAlert } from 'app/services/alerts';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { getCurrency } from 'app/utils/monetarioUtil';
import React, { useEffect } from 'react';
import { ContasPagarAction, useContasPagar } from '../../store';
import { RecorrenciaAction, useRecorrencia } from '../../store/recorrencia';
import { recorrenciaStyles } from './recorrenciaStyles';

const FormListaRecorrencia = () => {
  const [state, dispatch] = useRecorrencia();
  const { page, selecionados, carregandoRegistros, search, requestParams } = state;
  const classes = recorrenciaStyles();

  useEffect(() => {
    RecorrenciaAction.carregarRegistros(dispatch, {
      ...requestParams,
      dataVencimentoInicio: search?.dataVencimentoInicio?.format('YYYY-MM-DD'),
      dataVencimentoFim: search?.dataVencimentoFim?.format('YYYY-MM-DD'),
    });
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      name: 'dataVencimento',
      title: 'Vencimento',
      getCellValue: (data) => {
        return (
          <div>
            {DataBR(data.dataVencimento)}
            {data.retencoes?.length > 0 && (
              <Tooltip title="Despesa possui retenção de impostos" placement="top">
                <div className={classes.badge}>R</div>
              </Tooltip>
            )}
          </div>
        );
      },
      hideTooltip: true,
    },
    {
      name: 'fornecedor.pessoa.nome',
      title: 'Fornecedor',
      getCellValue: (data) => data.fornecedor?.pessoa.nome,
    },
    {
      name: 'valorBruto',
      title: 'Valor (R$)',
      getCellValue: (data) => {
        return (
          <div>
            {!data?.retencoes && getCurrency(data.valorLiquido).replace('R$', '')}
            {data?.retencoes?.length > 0 && getCurrency(data.valorBruto).replace('R$', '')}
          </div>
        );
      },
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      getCellValue: (data) => (
        <TableActionButtonDelete onClick={() => RecorrenciaAction.excluirRecorrencia(dispatch, data.id)} />
      ),
    },
  ];

  const onFetchData = (params) => {
    RecorrenciaAction.carregarRegistros(dispatch, {
      ...params,
      dataVencimentoInicio: search?.dataVencimentoInicio?.format('YYYY-MM-DD'),
      dataVencimentoFim: search?.dataVencimentoFim?.format('YYYY-MM-DD'),
    });
  };

  const onSelectionChange = (params) => dispatch(RecorrenciaAction.setSelecionados(params));

  const onCurrentPageChange = (params) => {
    dispatch(RecorrenciaAction.setCurrentPage(params));
  };

  const onPageSizeChange = (params) => dispatch(RecorrenciaAction.setPageSize(params));
  const onSortingChange = (params) => dispatch(RecorrenciaAction.setSorting(params));

  return (
    <div className={classes.container}>
      <div className={classes.periodoVencimentoContainer}>
        <RangeDateInput
          id="periodo.dataVencimentoInicio"
          value={[search.dataVencimentoInicio, search.dataVencimentoFim]}
          label="Período do vencimento "
          onChange={(inicio, fim) => {
            dispatch(RecorrenciaAction.filtroVencimento(inicio, fim));
          }}
        />
        <Button
          className={classes.buttonSave}
          onClick={() => {
            RecorrenciaAction.carregarRegistros(dispatch, {
              ...requestParams,
              dataVencimentoInicio: search?.dataVencimentoInicio?.format('YYYY-MM-DD'),
              dataVencimentoFim: search?.dataVencimentoFim?.format('YYYY-MM-DD'),
            });
          }}
        >
          <Typography>Buscar</Typography>
        </Button>
      </div>
      <SimpleTableBase
        pageType="async"
        // fetchOnCreate
        defaultSorting={[{ columnName: 'dataVencimento', direction: 'desc' }]}
        columns={columns}
        rows={page.content}
        loading={carregandoRegistros}
        totalCount={state.page.totalElements}
        searchBy={state.requestParams.q}
        fetchData={onFetchData}
        onSelectionChange={onSelectionChange}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        selection={selecionados}
        selectionType={state.tipoSelecao}
        onSelectionTypeChange={(params) => {
          dispatch(RecorrenciaAction.setTipoSelecao(params));
        }}
      />
    </div>
  );
};

const ContasPagarRecorrencia = () => {
  const [state, dispatch] = useRecorrencia();
  const [, dispatchContasPagar] = useContasPagar();
  const { visualizarRecorrencia, selecionados, tipoSelecao, search } = state;

  const handleCancelar = () => {
    const close = () => {
      RecorrenciaAction.fecharModal(dispatch);
    };

    if (selecionados.length > 0) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const handleResgatar = async () => {
    if ((selecionados && selecionados.length > 0) || tipoSelecao === 'TODOS_FILTRO') {
      eventReactGA('Conta a pagar', 'Resgatar contas a pagar agendadas');
      await RecorrenciaAction.resgatar(
        dispatch,
        selecionados.map((s) => s.id),
        tipoSelecao,
        search
      );
      RecorrenciaAction.fecharModal(dispatch);
      ContasPagarAction.carregarRegistros(dispatchContasPagar);
    }
  };

  return (
    <SimpleDialog open={visualizarRecorrencia} onClose={handleCancelar}>
      {visualizarRecorrencia && (
        <SimpleDialogContent
          header={{
            title: 'Despesas regulares',

            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="button" onClick={handleResgatar}>
                  Resgatar
                </SaveButton>
              </>
            ),
          }}
          content={<FormListaRecorrencia />}
          contentStyle={{ padding: 0 }}
        />
      )}
    </SimpleDialog>
  );
};

export default ContasPagarRecorrencia;
